/**
 * Props - the things you knock over. The core verb of the game lives here.
 *
 * Props are plain DATA RECORDS driven by one PropSystem, not per-object classes.
 * A level has 60-80 of them, they are the main draw-call and physics cost, and
 * pooling/instancing only works if they share a single update path. Characters
 * get classes (see actor.ts); props do not. This asymmetry is deliberate - see
 * ARCHITECTURE.md.
 *
 * TWO EVENTS MATTER, and they are separate on purpose:
 *   - "knocked": the prop moved meaningfully from where it was resting. Fires the
 *     mischief action. A nudge does not count; shoving it off a table does.
 *   - "broken": the prop landed hard enough to shatter. Fires prop:broken and
 *     may reveal a mischief key.
 * Knocking a mug onto carpet is mischief without breakage. Shattering it on tile
 * is both. Keeping them separate is what makes the mischief catalog's
 * noise/heat/trace split expressive.
 */

import * as THREE from 'three';
import type RAPIER from '@dimforge/rapier3d-compat';
import type { PropDef } from '@/levels/schema';
import type { Vec3 } from '@/core/math';
import { clone, distance, vec3 } from '@/core/math';
import { tuning } from '@/core/tuning';
import { getPhysics, getSize, createAssetObject } from '@/engine/assetRegistry';
import { addOutlinesRecursive } from '@/render/outline';
import type { PhysicsWorld } from '@/engine/physicsWorld';

export interface PropInstance {
  id: string;
  def: PropDef;
  object: THREE.Object3D;
  body: RAPIER.RigidBody | null;
  size: Vec3;
  mass: number;
  /** Impact speed (m/s) above which this prop shatters. 0 means unbreakable. */
  breakSpeed: number;
  mischief: string | undefined;
  carryable: boolean;

  /** Where it started, so displacement can be measured. */
  restingPosition: Vec3;
  knocked: boolean;
  broken: boolean;
  carried: boolean;
  /** Highest speed seen since it was last at rest - the impact energy proxy. */
  peakSpeed: number;
  /** Seconds spent settled, used to sleep the body and reclaim solver time. */
  settledFor: number;
}

export interface PropEvents {
  /** Moved meaningfully from rest. Fires mid-fall, so use it for counting only. */
  onKnocked: (prop: PropInstance) => void;
  /**
   * Came to rest after a real fall. THIS is where mischief is resolved, because
   * it is the moment the crash actually happens - firing the 70-loudness shatter
   * sound while the vase is still in the air would be badly mistimed.
   */
  onSettled: (prop: PropInstance, broke: boolean) => void;
  onBroken: (prop: PropInstance) => void;
}

export class PropSystem {
  readonly props: PropInstance[] = [];
  private byId = new Map<string, PropInstance>();

  constructor(
    private readonly scene: THREE.Scene,
    private readonly physics: PhysicsWorld,
    private readonly events: PropEvents,
  ) {}

  spawn(def: PropDef): PropInstance {
    const [w, h, d] = getSize(def.asset);
    const size = vec3(w, h, d);
    const physicsDef = getPhysics(def.asset);
    const mass = def.mass ?? physicsDef.mass;
    const base = vec3(def.position[0], def.position[1], def.position[2]);

    const object = createAssetObject(def.asset);
    object.position.set(base.x, base.y, base.z);
    if (def.rotationY) object.rotation.y = def.rotationY;
    object.name = `prop:${def.id}`;
    // Props get outlines too - a vase without an outline next to an outlined cat
    // looks like a bug rather than a style.
    addOutlinesRecursive(object);
    this.scene.add(object);

    const solid = def.solid ?? true;
    let body: RAPIER.RigidBody | null = null;
    if (!solid) {
      // No collider at all. Used for props the cat must get INSIDE (an open box
      // that doubles as a hide spot) and for pure decoration.
      body = null;
    } else if (def.dynamic ?? true) {
      const created = this.physics.addDynamicBox({
        position: base,
        size,
        mass,
        rotationY: def.rotationY,
      });
      body = created.body;
    } else {
      // Static props still need a collider so the cat can stand on them.
      this.physics.addStaticBox({
        position: vec3(base.x, base.y + h / 2, base.z),
        size,
        rotationY: def.rotationY,
      });
    }

    const breakable = def.breakable ?? physicsDef.breakable;
    const breakForce = physicsDef.breakForce ?? 10;

    const prop: PropInstance = {
      id: def.id,
      def,
      object,
      body,
      size,
      mass,
      breakSpeed: breakable ? breakForce / tuning.props.breakSpeedDivisor : 0,
      mischief: def.mischief,
      carryable: def.carryable ?? false,
      restingPosition: clone(base),
      knocked: false,
      broken: false,
      carried: false,
      peakSpeed: 0,
      settledFor: 0,
    };

    this.props.push(prop);
    this.byId.set(prop.id, prop);
    return prop;
  }

  get(id: string): PropInstance | undefined {
    return this.byId.get(id);
  }

  /**
   * One fixed step. Reads transforms out of Rapier, detects knocks and breaks.
   * Called AFTER physics.step() so the transforms are current.
   */
  step(dt: number): void {
    for (const prop of this.props) {
      if (!prop.body || prop.broken || prop.carried) continue;

      const translation = prop.body.translation();
      const rotation = prop.body.rotation();

      // Rapier bodies are centered; the visual origin is at the base.
      prop.object.position.set(translation.x, translation.y - prop.size.y / 2, translation.z);
      prop.object.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);

      const velocity = prop.body.linvel();
      const speed = Math.hypot(velocity.x, velocity.y, velocity.z);
      if (speed > prop.peakSpeed) prop.peakSpeed = speed;

      // Knock detection: displacement from where it was resting.
      if (!prop.knocked) {
        const moved = distance(
          { x: translation.x, y: translation.y - prop.size.y / 2, z: translation.z },
          prop.restingPosition,
        );
        if (moved > tuning.props.knockedDisplacement) {
          prop.knocked = true;
          this.events.onKnocked(prop);
        }
      }

      // Landing resolution: it has come to rest after a real fall.
      if (speed < tuning.props.restSpeed) {
        prop.settledFor += dt;
        if (prop.peakSpeed > tuning.props.minFallSpeedToResolve) {
          const shouldBreak = prop.breakSpeed > 0 && prop.peakSpeed >= prop.breakSpeed;
          prop.peakSpeed = 0;
          this.events.onSettled(prop, shouldBreak);
          if (shouldBreak) this.break(prop);
        }
        // Sleeping settled bodies keeps the solver cheap once a room is trashed.
        if (prop.settledFor > tuning.props.sleepAfterSeconds && !prop.body.isSleeping()) {
          prop.body.sleep();
        }
      } else {
        prop.settledFor = 0;
      }
    }
  }

  /** Shatters a prop: hides the mesh, removes the body, notifies listeners. */
  break(prop: PropInstance): void {
    if (prop.broken) return;
    prop.broken = true;
    prop.object.visible = false;
    if (prop.body) {
      this.physics.world.removeRigidBody(prop.body);
      prop.body = null;
    }
    this.events.onBroken(prop);
  }

  /** Picks a prop up (heist objectives). Its body is removed while carried. */
  pickUp(prop: PropInstance): boolean {
    if (!prop.carryable || prop.carried || prop.broken) return false;
    prop.carried = true;
    if (prop.body) {
      this.physics.world.removeRigidBody(prop.body);
      prop.body = null;
    }
    return true;
  }

  /** Drops a carried prop at a world position, restoring its physics body. */
  drop(prop: PropInstance, at: Vec3): void {
    if (!prop.carried) return;
    prop.carried = false;
    const created = this.physics.addDynamicBox({ position: at, size: prop.size, mass: prop.mass });
    prop.body = created.body;
    prop.object.position.set(at.x, at.y, at.z);
  }

  /** Props whose mischief has fired. Drives the "knock over 3 things" objective. */
  get knockedCount(): number {
    return this.props.filter((p) => p.knocked).length;
  }

  get brokenCount(): number {
    return this.props.filter((p) => p.broken).length;
  }

  /** The nearest prop the cat could interact with, within `radius`. */
  nearest(
    position: Vec3,
    radius: number,
    filter?: (prop: PropInstance) => boolean,
  ): PropInstance | undefined {
    let best: PropInstance | undefined;
    let bestDist = radius;
    for (const prop of this.props) {
      if (prop.broken || prop.carried) continue;
      if (filter && !filter(prop)) continue;
      const dist = distance(position, prop.object.position);
      if (dist < bestDist) {
        bestDist = dist;
        best = prop;
      }
    }
    return best;
  }

  clear(): void {
    for (const prop of this.props) {
      this.scene.remove(prop.object);
      if (prop.body) this.physics.world.removeRigidBody(prop.body);
    }
    this.props.length = 0;
    this.byId.clear();
  }
}
